import { useMemo } from 'react';

import {
  RedeemableTicket,
  useMarkEventTicketUnused,
  useRedeemEventTicket,
} from '@givebacks/api/store';
import { CellContext } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, notifications } from 'ui';

import { EventCheckInTableInstance } from './instance';

type OptionsProps = {
  causeId: string;
  ticketId: string;
};

const Options = ({ causeId, ticketId }: OptionsProps) => {
  const { mutate } = useMarkEventTicketUnused();
  return (
    <EventCheckInTableInstance.Table.RowAction.Option
      label="Mark Unused"
      onClick={() =>
        mutate({
          ticketId: ticketId,
          causeId: causeId,
        })
      }
    />
  );
};

const RedeemableTicketsRowActions = ({ row }: CellContext<RedeemableTicket, unknown>) => {
  const { table } = EventCheckInTableInstance.useContext();

  const { mutate, isPending: isLoading } = useRedeemEventTicket();

  const causeId = table.options.meta?.causeId;

  const { original } = row;
  const isRedeemed = useMemo(() => original.fulfilled_at != null, [original.fulfilled_at]);
  const isRefunded = useMemo(() => !!original.deleted_at, [original.deleted_at]);

  if (!causeId || isRefunded) return null;
  if (isRedeemed) {
    return (
      <EventCheckInTableInstance.Table.RowAction>
        <Options causeId={causeId} ticketId={original.uuid} />
      </EventCheckInTableInstance.Table.RowAction>
    );
  }

  return (
    <ButtonMantine
      onClick={() =>
        mutate(
          {
            ticketId: original.uuid,
            causeId: causeId,
          },
          {
            onSuccess: () => {
              notifications.show({
                message: 'Ticket Checked In',
                variant: 'success',
              });
            },
            onError: (error) => {
              notifications.show({
                message: getErrorMessage_v2(error, 'Failed to check in ticket'),
                variant: 'error',
              });
            },
          }
        )
      }
      size="compact-xs"
      loading={isLoading}
    >
      Check In
    </ButtonMantine>
  );
};

export { RedeemableTicketsRowActions };
